import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Typography,
  Alert,
  Grid,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { Save } from '@mui/icons-material';
import { systemConfigAPI } from '../../services/api';

const CONFIG_KEY = 'deployment';

const defaultDeployment = {
  deployment_mode: 'laptop',
  jetson_ip: '192.168.2.62',
  audio_support_hardware: true,
  audio_support_browser: true,
  cabin_config_path: 'config/cabin_assignments.yaml',
  camera_device: '/dev/video0',
  face_recognition_enabled: false,
  face_recognition_model: 'Facenet',
  face_recognition_detector: 'opencv',
  face_database_path: '/mnt/ssd/ambient_ai/face_database',
};

export default function DeploymentJetson() {
  const [values, setValues] = useState<Record<string, unknown>>(defaultDeployment);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await systemConfigAPI.get(CONFIG_KEY);
      const data = res.data?.config_value_json ?? defaultDeployment;
      setValues({ ...defaultDeployment, ...data } as Record<string, unknown>);
    } catch {
      setValues(defaultDeployment as Record<string, unknown>);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await systemConfigAPI.set(CONFIG_KEY, values);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const update = (key: string, value: unknown) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  if (loading) return <Typography>Loading…</Typography>;

  return (
    <Box>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        Deployment & Jetson. Mode, audio, cabin config, camera, face recognition. Stored in system_config. Restart may be required.
      </Typography>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Mode</InputLabel>
              <Select
                value={String(values.deployment_mode ?? 'laptop')}
                label="Mode"
                onChange={(e) => update('deployment_mode', e.target.value)}
              >
                <MenuItem value="laptop">Laptop</MenuItem>
                <MenuItem value="jetson">Jetson</MenuItem>
                <MenuItem value="server">Server</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Jetson IP (if Jetson mode)"
              value={String(values.jetson_ip ?? '')}
              onChange={(e) => update('jetson_ip', e.target.value)}
              placeholder="192.168.2.62"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(values.audio_support_hardware)}
                  onChange={(e) => update('audio_support_hardware', e.target.checked)}
                />
              }
              label="Audio: Hardware (USB)"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(values.audio_support_browser)}
                  onChange={(e) => update('audio_support_browser', e.target.checked)}
                />
              }
              label="Audio: Browser"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Cabin config path"
              value={String(values.cabin_config_path ?? '')}
              onChange={(e) => update('cabin_config_path', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Camera device"
              value={String(values.camera_device ?? '')}
              onChange={(e) => update('camera_device', e.target.value)}
              placeholder="/dev/video0"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(values.face_recognition_enabled)}
                  onChange={(e) => update('face_recognition_enabled', e.target.checked)}
                />
              }
              label="Face recognition enabled"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Face model</InputLabel>
              <Select
                value={String(values.face_recognition_model ?? 'Facenet')}
                label="Face model"
                onChange={(e) => update('face_recognition_model', e.target.value)}
              >
                <MenuItem value="Facenet">Facenet</MenuItem>
                <MenuItem value="VGG-Face">VGG-Face</MenuItem>
                <MenuItem value="ArcFace">ArcFace</MenuItem>
                <MenuItem value="Facenet512">Facenet512</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Face detector</InputLabel>
              <Select
                value={String(values.face_recognition_detector ?? 'opencv')}
                label="Face detector"
                onChange={(e) => update('face_recognition_detector', e.target.value)}
              >
                <MenuItem value="opencv">opencv</MenuItem>
                <MenuItem value="ssd">ssd</MenuItem>
                <MenuItem value="mtcnn">mtcnn</MenuItem>
                <MenuItem value="retinaface">retinaface</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Face database path"
              value={String(values.face_database_path ?? '')}
              onChange={(e) => update('face_database_path', e.target.value)}
            />
          </Grid>
        </Grid>
        <Button variant="contained" startIcon={<Save />} onClick={handleSave} disabled={saving} sx={{ mt: 2 }}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </Paper>
    </Box>
  );
}
